import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const ProgressContext = createContext();

export const useProgress = () => {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error('useProgress must be used within a ProgressProvider');
  }
  return context;
};

export const ProgressProvider = ({ children }) => {
  const { user } = useAuth();
  const [progress, setProgress] = useState({
    modules: {
      1: { completed: false, sections: {}, quizScore: null, quizCompleted: false },
      2: { completed: false, sections: {}, quizScore: null, quizCompleted: false },
      3: { completed: false, sections: {}, quizScore: null, quizCompleted: false },
      4: { completed: false, sections: {}, quizScore: null, quizCompleted: false },
      5: { completed: false, sections: {}, quizScore: null, quizCompleted: false },
      6: { completed: false, sections: {}, quizScore: null, quizCompleted: false }
    },
    certificateUnlocked: false,
    certificateDate: null,
    totalProgress: 0
  });

  // Load progress from localStorage on mount
  useEffect(() => {
    if (user?.email) {
      const savedProgress = localStorage.getItem(`f12ia_progress_${user.email}`);
      if (savedProgress) {
        setProgress(JSON.parse(savedProgress));
      }
    }
  }, [user]);

  // Save progress to localStorage whenever it changes
  useEffect(() => {
    if (user?.email) {
      localStorage.setItem(`f12ia_progress_${user.email}`, JSON.stringify(progress));
    }
  }, [progress, user]);

  // Calculate total progress percentage
  const calculateTotalProgress = (modules) => {
    let totalSections = 0;
    let completedSections = 0;

    Object.values(modules).forEach(module => {
      const sectionCount = Object.keys(module.sections).length || 5; // Default 5 sections per module
      totalSections += sectionCount + 1; // +1 for quiz
      completedSections += Object.values(module.sections).filter(Boolean).length;
      if (module.quizCompleted) completedSections += 1;
    });

    return totalSections > 0 ? Math.round((completedSections / (6 * 6)) * 100) : 0; // 6 modules * 6 items each
  };

  // Mark a section as completed
  const completeSection = (moduleId, sectionId) => {
    setProgress(prev => {
      const newModules = {
        ...prev.modules,
        [moduleId]: {
          ...prev.modules[moduleId],
          sections: {
            ...prev.modules[moduleId].sections,
            [sectionId]: true
          }
        }
      };

      const newTotalProgress = calculateTotalProgress(newModules);

      return {
        ...prev,
        modules: newModules,
        totalProgress: newTotalProgress
      };
    });
  };

  // Toggle section completion
  const toggleSection = (moduleId, sectionId) => {
    setProgress(prev => {
      const currentValue = prev.modules[moduleId]?.sections[sectionId] || false;
      const newModules = {
        ...prev.modules,
        [moduleId]: {
          ...prev.modules[moduleId],
          sections: {
            ...prev.modules[moduleId].sections,
            [sectionId]: !currentValue
          }
        }
      };

      const newTotalProgress = calculateTotalProgress(newModules);

      return {
        ...prev,
        modules: newModules,
        totalProgress: newTotalProgress
      };
    });
  };

  // Complete quiz with score
  const completeQuiz = (moduleId, score) => {
    setProgress(prev => {
      const newModules = {
        ...prev.modules,
        [moduleId]: {
          ...prev.modules[moduleId],
          quizScore: score,
          quizCompleted: true,
          completed: score >= 70 // Module completed if quiz score >= 70%
        }
      };

      const newTotalProgress = calculateTotalProgress(newModules);

      // Check if all modules are completed for certificate
      const allCompleted = Object.values(newModules).every(m => m.completed);

      return {
        ...prev,
        modules: newModules,
        totalProgress: newTotalProgress,
        certificateUnlocked: allCompleted,
        certificateDate: allCompleted && !prev.certificateUnlocked ? new Date().toISOString() : prev.certificateDate
      };
    });
  };

  // Get module progress percentage
  const getModuleProgress = (moduleId) => {
    const module = progress.modules[moduleId];
    if (!module) return 0;

    const sectionsCompleted = Object.values(module.sections).filter(Boolean).length;
    const totalItems = 5 + 1; // 5 sections + 1 quiz
    const quizDone = module.quizCompleted ? 1 : 0;

    return Math.round(((sectionsCompleted + quizDone) / totalItems) * 100);
  };

  // Check if section is completed
  const isSectionCompleted = (moduleId, sectionId) => {
    return progress.modules[moduleId]?.sections[sectionId] || false;
  };

  // Reset progress (for testing)
  const resetProgress = () => {
    setProgress({
      modules: {
        1: { completed: false, sections: {}, quizScore: null, quizCompleted: false },
        2: { completed: false, sections: {}, quizScore: null, quizCompleted: false },
        3: { completed: false, sections: {}, quizScore: null, quizCompleted: false },
        4: { completed: false, sections: {}, quizScore: null, quizCompleted: false },
        5: { completed: false, sections: {}, quizScore: null, quizCompleted: false },
        6: { completed: false, sections: {}, quizScore: null, quizCompleted: false }
      },
      certificateUnlocked: false,
      certificateDate: null,
      totalProgress: 0
    });
  };

  const value = {
    progress,
    completeSection,
    toggleSection,
    completeQuiz,
    getModuleProgress,
    isSectionCompleted,
    resetProgress
  };

  return (
    <ProgressContext.Provider value={value}>
      {children}
    </ProgressContext.Provider>
  );
};

export default ProgressContext;
